/**
 * Build-time internal links rendered above the closing CTA (see JgCta):
 * - /blog/<slug>                → "Related posts"
 * - /services/<slug>            → "From the blog"
 * - /case-studies/<cat>/<slug>  → "From the blog"
 *
 * Data: src/data/blogLinks.json, generated by scripts/build-blog-links.mjs
 * (indexable posts only, deterministic ranking). The prerendered HTML for
 * the same routes carries the same links, so crawlers and users match.
 * Loaded as a separate chunk so pages without the block don't pay for it.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { formatBlogPublishedDate } from "../../utils/wisp";

let cached = null;
let pending = null;

function loadBlogLinks() {
  if (cached) return Promise.resolve(cached);
  if (!pending) {
    pending = import("../../data/blogLinks.json")
      .then((mod) => {
        cached = mod.default || mod;
        return cached;
      })
      .catch(() => {
        pending = null;
        return null;
      });
  }
  return pending;
}

function blockForPath(pathname, data) {
  if (!data) return null;
  const p = String(pathname || "").replace(/\/+$/, "");
  const pick = (idx) =>
    (Array.isArray(idx) ? idx : [])
      .map((i) => data.listing?.[i])
      .filter(Boolean);

  let m = p.match(/^\/blog\/([^/]+)$/);
  if (m) {
    let slug = m[1];
    try {
      slug = decodeURIComponent(slug);
    } catch {
      /* keep raw */
    }
    return { heading: "Related posts", items: pick(data.related?.[slug]) };
  }
  m = p.match(/^\/services\/([^/]+)$/);
  if (m) {
    return { heading: "From the blog", items: pick(data.services?.[m[1].toLowerCase()]) };
  }
  if (/^\/case-studies\/[^/]+\/[^/]+$/.test(p)) {
    return { heading: "From the blog", items: pick(data.caseStudies?.[p]) };
  }
  return null;
}

const wantsBlock = (pathname) =>
  /^\/(?:blog\/[^/]+|services\/[^/]+|case-studies\/[^/]+\/[^/]+)\/?$/.test(
    String(pathname || "")
  );

const truncateToWords = (text, wordCount = 15) => {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (words.length <= wordCount) return words.join(" ");
  return `${words.slice(0, wordCount).join(" ").replace(/…$/, "")}...`;
};

const JgRelatedReading = () => {
  const { pathname } = useLocation();
  const active = wantsBlock(pathname);
  const [data, setData] = useState(cached);

  useEffect(() => {
    if (!active || data) return undefined;
    let alive = true;
    loadBlogLinks().then((d) => {
      if (alive && d) setData(d);
    });
    return () => {
      alive = false;
    };
  }, [active, data]);

  if (!active) return null;
  const block = blockForPath(pathname, data);
  if (!block || block.items.length === 0) return null;

  return (
    <section className="jg-section-pad" aria-label={block.heading}>
      <div className="jg-wrap">
        <div className="jg-section-head jg-reveal">
          <h2>{block.heading}</h2>
          <span className="num">Journal</span>
        </div>
        <ul className="jg-ledger jg-ledger--journal">
          {block.items.map((post) => {
            const label = formatBlogPublishedDate(post.d);
            return (
              <li key={post.s} className="jg-reveal">
                <Link to={`/blog/${post.s}`}>
                  <span className="idx">
                    {label ? <time dateTime={post.d}>{label}</time> : "—"}
                  </span>
                  <span className="name">{post.t}</span>
                  <span className="desc">
                    {truncateToWords(post.x)}
                    {label ? (
                      <span className="jg-ledger-date-end">
                        {" · "}
                        <time dateTime={post.d}>{label}</time>
                      </span>
                    ) : null}
                  </span>
                  <span className="arrow" aria-hidden="true">
                    ↗
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
        <p style={{ marginTop: "1.5rem" }}>
          <Link to="/blog" className="jg-link">
            All articles →
          </Link>
        </p>
      </div>
    </section>
  );
};

export default JgRelatedReading;
